// src/pages/admin/AdminRegisterPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight,
  Check,
  Building2,
  Info,
  Briefcase,
  DollarSign,
  Clock,
  FileText,
  AlertCircle,
  Loader2,
  Shield,
  Key,
  Smartphone,
  Image,
  Upload,
  X
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/AdminRegisterPage.module.css';

// Import Redux actions and selectors
import { 
  adminCreateAdmin,
  adminCheckAuth,
  selectIsAuthenticated,
  selectCurrentUser,
  selectAdminLoading,
  selectAdminError,
  selectAdminUpdateSuccess,
  resetAdminState,
  clearErrors
} from '../../store/slices/adminSlice';

const AdminRegisterPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentLanguage } = useLanguage();
  
  // Redux state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser = useSelector(selectCurrentUser);
  const adminLoading = useSelector(selectAdminLoading);
  const adminError = useSelector(selectAdminError);
  const adminUpdateSuccess = useSelector(selectAdminUpdateSuccess);
  
  // Translation hooks
  const { translatedText: t } = useTranslation();
  const { translatedText: errorTitle } = useTranslation('Error');
  const { translatedText: fillAllFields } = useTranslation('Please fill in all fields');
  const { translatedText: passwordMismatch } = useTranslation('Passwords do not match');
  const { translatedText: emailInvalid } = useTranslation('Please enter a valid email address');
  const { translatedText: phoneInvalid } = useTranslation('Please enter a valid phone number');
  const { translatedText: passwordMinLength } = useTranslation('Password must be at least 6 characters');
  const { translatedText: nameRequired } = useTranslation('Name is required');
  const { translatedText: registrationSuccess } = useTranslation('Registration successful!');
  const { translatedText: redirecting } = useTranslation('Redirecting to dashboard...');
  const { translatedText: uploadProfileImage } = useTranslation('Upload Profile Image');
  const { translatedText: profileImagePreviewText } = useTranslation('Profile Image Preview');
  const { translatedText: removeImage } = useTranslation('Remove Image');
  const { translatedText: passwordStrengthText } = useTranslation('Password Strength');
  const { translatedText: weak } = useTranslation('Weak');
  const { translatedText: fair } = useTranslation('Fair');
  const { translatedText: good } = useTranslation('Good');
  const { translatedText: strong } = useTranslation('Strong');
  const { translatedText: veryStrong } = useTranslation('Very Strong');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'admin'
  });
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [success, setSuccess] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  
  // Check if already authenticated
  useEffect(() => {
    dispatch(adminCheckAuth());
  }, [dispatch]);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, currentUser, navigate]);
  
  // Handle successful registration
  useEffect(() => {
    if (adminUpdateSuccess) {
      setSuccess(true);
      // Redirect to admin dashboard after 2 seconds
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 2000);
    }
  }, [adminUpdateSuccess, navigate]);
  
  // Handle errors from Redux - preserve existing errors
  useEffect(() => {
    if (adminError) {
      setErrors(prev => ({
        ...prev,
        general: adminError,
      }));
    }
  }, [adminError]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetAdminState());
      dispatch(clearErrors());
      // Clean up object URLs to prevent memory leaks
      if (profileImagePreview) {
        URL.revokeObjectURL(profileImagePreview);
      }
    };
  }, [dispatch, profileImagePreview]);
  
  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value || value.trim().length < 2) {
          return nameRequired || 'Name must be at least 2 characters';
        }
        if (value.trim().length > 50) {
          return 'Name must be less than 50 characters';
        }
        return '';
        
      case 'email':
        if (!value) {
          return 'Email is required';
        }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
          return emailInvalid || 'Please enter a valid email address';
        }
        return '';
        
      case 'phone':
        if (!value) {
          return 'Phone number is required';
        }
        const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
        if (!phoneRegex.test(value)) {
          return phoneInvalid || 'Please enter a valid phone number';
        }
        return '';
        
      case 'password':
        if (!value) {
          return 'Password is required';
        }
        if (value.length < 6) {
          return passwordMinLength || 'Password must be at least 6 characters';
        }
        if (value.length > 30) {
          return 'Password must be less than 30 characters';
        }
        return '';
        
      case 'confirmPassword':
        if (!value) {
          return 'Please confirm your password';
        }
        if (value !== formData.password) {
          return passwordMismatch || 'Passwords do not match';
        }
        return '';
        
      default:
        return '';
    }
  };
  
  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    const fields = ['name', 'email', 'phone', 'password', 'confirmPassword'];
    
    fields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear ONLY this field's validation error immediately
    setErrors(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [name]: true }));
  };
  
  // Handle blur for validation
  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name, value);
    
    setErrors(prev => {
      const next = { ...prev };
      if (error) {
        next[name] = error;
      } else {
        delete next[name];
      }
      return next;
    });
  };
  
  // Handle profile image upload - IMPROVED
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Clear any previous image preview to prevent memory leaks
    if (profileImagePreview) {
      URL.revokeObjectURL(profileImagePreview);
    }
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ 
        ...prev, 
        profileImage: 'Please upload a valid image file (JPEG, PNG, WEBP, GIF)' 
      }));
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ 
        ...prev, 
        profileImage: 'Image size must be less than 10MB' 
      }));
      return;
    }
    
    // Set loading state
    setIsImageUploading(true);
    
    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setProfileImage(file);
      setProfileImagePreview(previewUrl);
      setErrors(prev => ({ ...prev, profileImage: '' }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setErrors(prev => ({ 
        ...prev, 
        profileImage: 'Failed to upload image. Please try again.' 
      }));
    } finally {
      setIsImageUploading(false);
    }
    
    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };
  
  // Remove profile image - IMPROVED
  const handleRemoveImage = () => {
    // Revoke the object URL to free memory
    if (profileImagePreview) {
      URL.revokeObjectURL(profileImagePreview);
    }
    
    setProfileImage(null);
    setProfileImagePreview(null);
    setErrors(prev => ({ ...prev, profileImage: '' }));
    setIsImageUploading(false);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({});
    
    // Validate form
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector(`.${styles.error}`);
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare registration data
      const registrationData = {
        name: formData.name.trim(),
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone.trim(),
        password: formData.password,
        role: 'admin',
        language: currentLanguage || 'ht'
      };
      
      // If profile image is selected, add it to form data
      if (profileImage) {
        // Convert image to base64 or prepare for upload
        // Option 1: Convert to base64
        const reader = new FileReader();
        const imageData = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(profileImage);
        });
        registrationData.profileImageBase64 = imageData;
        registrationData.profileImageName = profileImage.name;
        registrationData.profileImageSize = profileImage.size;
        registrationData.profileImageType = profileImage.type;
      }
      
      // Dispatch Redux action to create admin
      const result = await dispatch(adminCreateAdmin(registrationData)).unwrap();
      
      if (result.success) {
        // Success is handled by the useEffect
        console.log('Admin created successfully:', result.data);
      }
    } catch (error) {
      // Error is handled by the useEffect
      console.error('Registration error:', error);
      setErrors({ 
        general: error.message || 'An unexpected error occurred. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: colors.gray400 };
    
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    
    const strengths = [
      { label: weak || 'Weak', color: colors.error },
      { label: fair || 'Fair', color: colors.warning },
      { label: good || 'Good', color: colors.accentGold },
      { label: strong || 'Strong', color: colors.success },
      { label: veryStrong || 'Very Strong', color: colors.success }
    ];
    
    const index = Math.min(Math.floor(score / 2), strengths.length - 1);
    return { score, ...strengths[index] };
  };
  
  const passwordStrength = getPasswordStrength(formData.password);
  
  // Check if form is loading
  const isLoading = loading || adminLoading;
  
  return (
    <div className={styles.container}>
      {/* Background Decoration */}
      <div className={styles.backgroundDecoration}>
        <div className={styles.decorationCircle1} />
        <div className={styles.decorationCircle2} />
        <div className={styles.decorationCircle3} />
      </div>
      
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <Link to="/" className={styles.backButton}>
            <ArrowLeft size={20} />
            <span>Back</span>
          </Link>
          
          <div className={styles.headerContent}>
            <div className={styles.iconWrapper}>
              <Shield size={32} color={colors.white} />
            </div>
            <h1 className={styles.title}>
              Admin Registration
            </h1>
            <p className={styles.subtitle}>
              Create your administrator account
            </p>
          </div>
        </div>
        
        {/* Success Message */}
        {success && (
          <div className={styles.successMessage}>
            <Check size={24} />
            <div>
              <h3>{registrationSuccess || 'Registration successful!'}</h3>
              <p>{redirecting || 'Redirecting to dashboard...'}</p>
            </div>
          </div>
        )}
        
        {/* General Error */}
        {errors.general && (
          <div className={styles.generalError}>
            <AlertCircle size={20} />
            <span>{errors.general}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Profile Image Upload - IMPROVED */}
          <div className={styles.imageUploadSection}>
            <label className={styles.imageLabel}>
              {uploadProfileImage || 'Profile Image'}
            </label>
            <div className={styles.imageUploadContainer}>
              {profileImagePreview ? (
                <div className={styles.imagePreviewWrapper}>
                  <img 
                    src={profileImagePreview} 
                    alt={profileImagePreviewText || 'Profile Image Preview'}
                    className={styles.imagePreview}
                  />
                  <div className={styles.imageActions}>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className={styles.removeImageButton}
                      disabled={isImageUploading}
                    >
                      <X size={16} />
                      <span>{removeImage || 'Remove'}</span>
                    </button>
                    <label className={styles.changeImageButton}>
                      <Upload size={16} />
                      <span>Change</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className={styles.uploadInput}
                        disabled={isImageUploading}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <label className={styles.uploadLabel}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className={styles.uploadInput}
                    disabled={isImageUploading || isLoading}
                  />
                  <div className={styles.uploadPlaceholder}>
                    {isImageUploading ? (
                      <Loader2 size={32} className={styles.spinner} />
                    ) : (
                      <Upload size={32} color={colors.gray400} />
                    )}
                    <span>{isImageUploading ? 'Uploading...' : (uploadProfileImage || 'Click to upload')}</span>
                    <small>PNG, JPG, WEBP (Max 10MB)</small>
                  </div>
                </label>
              )}
              {errors.profileImage && (
                <span className={styles.errorText}>{errors.profileImage}</span>
              )}
            </div>
          </div>
          
          {/* Name Field */}
          <div className={styles.formGroup}>
            <label htmlFor="name" className={styles.label}>
              <User size={18} />
              <span>Full Name</span>
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your full name"
                className={`${styles.input} ${touched.name && errors.name ? styles.inputError : ''}`}
                disabled={isLoading}
              />
            </div>
            {touched.name && errors.name && (
              <span className={styles.errorText}>{errors.name}</span>
            )}
          </div>
          
          {/* Email Field */}
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              <Mail size={18} />
              <span>Email Address</span>
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your email address"
                className={`${styles.input} ${touched.email && errors.email ? styles.inputError : ''}`}
                disabled={isLoading}
              />
            </div>
            {touched.email && errors.email && (
              <span className={styles.errorText}>{errors.email}</span>
            )}
          </div>
          
          {/* Phone Field */}
          <div className={styles.formGroup}>
            <label htmlFor="phone" className={styles.label}>
              <Phone size={18} />
              <span>Phone Number</span>
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your phone number"
                className={`${styles.input} ${touched.phone && errors.phone ? styles.inputError : ''}`}
                disabled={isLoading}
              />
            </div>
            {touched.phone && errors.phone && (
              <span className={styles.errorText}>{errors.phone}</span>
            )}
          </div>
          
          {/* Password Field */}
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              <Lock size={18} />
              <span>Password</span>
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Create a password"
                className={`${styles.input} ${touched.password && errors.password ? styles.inputError : ''}`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.eyeButton}
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {touched.password && errors.password && (
              <span className={styles.errorText}>{errors.password}</span>
            )}
            
            {/* Password Strength Indicator */}
            {formData.password && (
              <div className={styles.passwordStrength}>
                <div className={styles.strengthLabel}>
                  <span>{passwordStrengthText || 'Password Strength'}:</span>
                  <span style={{ color: passwordStrength.color }}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div className={styles.strengthBar}>
                  <div 
                    className={styles.strengthFill}
                    style={{
                      width: `${(passwordStrength.score / 5) * 100}%`,
                      backgroundColor: passwordStrength.color
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Confirm Password Field */}
          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              <Key size={18} />
              <span>Confirm Password</span>
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Confirm your password"
                className={`${styles.input} ${touched.confirmPassword && errors.confirmPassword ? styles.inputError : ''}`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className={styles.eyeButton}
                disabled={isLoading}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {touched.confirmPassword && errors.confirmPassword && (
              <span className={styles.errorText}>{errors.confirmPassword}</span>
            )}
          </div>
          
          {/* Admin Role Notice */}
          <div className={styles.notice}>
            <Info size={18} />
            <span>
              By creating an admin account, you will have full access to the 
              admin dashboard and all management features.
            </span>
          </div>
          
          {/* Submit Button */}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 size={20} className={styles.spinner} />
                <span>Creating account...</span>
              </>
            ) : (
              <>
                <span>Create Admin Account</span>
                <ArrowRight size={20} />
              </>
            )}
          </button>
          
          {/* Footer */}
          <div className={styles.footer}>
            <p>
              Already have an account?{' '}
              <Link to="/login" className={styles.loginLink}>
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminRegisterPage;